import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import { uploadUserIcon } from '../firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getDoc } from 'firebase/firestore';
import { validateImageFile, getUploadErrorMessage } from '../utils/imageValidation';
import './ProfileSetup.css';

const ProfileSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, updateUserProfile, userProfile, logout } = useAuth();
  const editMode = location.state?.editMode;
  
  const [formData, setFormData] = useState({
    nickname: '',
    gender: '',
    birthYear: '',
    birthMonth: '',
    birthDay: ''
  });
  const [iconPreview, setIconPreview] = useState(userProfile?.iconURL || userProfile?.photoURL || '/img/icon.png');
  const [iconFile, setIconFile] = useState(null);
  const [useGoogleIcon, setUseGoogleIcon] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 年の選択肢を生成（現在年-100歳から現在年-13歳まで）
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 100;
    const endYear = currentYear - 13;
    const years = [];
    for (let year = endYear; year >= startYear; year--) {
      years.push(year);
    }
    return years;
  };

  // 月の選択肢を生成
  const generateMonths = () => {
    return Array.from({ length: 12 }, (_, i) => i + 1);
  };

  // 日の選択肢を生成（選択された年月に応じて）
  const generateDays = () => {
    if (!formData.birthYear || !formData.birthMonth) return [];
    const year = parseInt(formData.birthYear);
    const month = parseInt(formData.birthMonth);
    const daysInMonth = new Date(year, month, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };

  // コンポーネントマウント時にGoogle情報を設定
  useEffect(() => {
    if (currentUser) {
      // Googleから取得した表示名をニックネームの初期値に設定
      if (currentUser.displayName) {
        setFormData(prev => ({
          ...prev,
          nickname: currentUser.displayName
        }));
      }
      // 最新アイコンURLがあれば初期表示
      if (userProfile?.iconURL) {
        setIconPreview(userProfile.iconURL);
      } else if (currentUser.photoURL) {
        setIconPreview(currentUser.photoURL);
        setUseGoogleIcon(true);
      }
    }
  }, [currentUser, userProfile]);

  // 編集モード時に既存ユーザー情報をフォームにセット
  useEffect(() => {
    if (editMode && userProfile) {
      // birthdateをYYYY-MM-DD形式に変換して年月日に分割
      let birthdate = userProfile.birthdate || '';
      let year = '', month = '', day = '';
      
      if (birthdate) {
        // yyyymmdd → yyyy-mm-dd
        if (/^\d{8}$/.test(birthdate)) {
          year = birthdate.slice(0,4);
          month = parseInt(birthdate.slice(4,6)).toString();
          day = parseInt(birthdate.slice(6,8)).toString();
        } else if (birthdate instanceof Date) {
          const date = new Date(birthdate);
          year = date.getFullYear().toString();
          month = (date.getMonth() + 1).toString();
          day = date.getDate().toString();
        } else if (birthdate.includes('/')) {
          const parts = birthdate.split('/');
          if (parts.length === 3) {
            year = parts[0];
            month = parseInt(parts[1]).toString();
            day = parseInt(parts[2]).toString();
          }
        } else if (birthdate.includes('-')) {
          const parts = birthdate.split('-');
          if (parts.length === 3) {
            year = parts[0];
            month = parseInt(parts[1]).toString();
            day = parseInt(parts[2]).toString();
          }
        }
      }
      
      setFormData({
        nickname: userProfile.nickname || userProfile.displayName || '',
        gender: userProfile.gender || '',
        birthYear: year,
        birthMonth: month,
        birthDay: day,
        icon: userProfile.photoURL || ''
      });
      // 最新アイコンURLがあればプレビューに反映
      if (userProfile.iconURL) {
        setIconPreview(userProfile.iconURL);
      } else if (userProfile.photoURL) {
        setIconPreview(userProfile.photoURL);
      } else {
        setIconPreview('/img/icon.png');
      }
    }
  }, [editMode, userProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // 年または月が変更された場合、日をリセット
      if ((name === 'birthYear' || name === 'birthMonth') && prev.birthDay) {
        const year = name === 'birthYear' ? parseInt(value) : parseInt(prev.birthYear);
        const month = name === 'birthMonth' ? parseInt(value) : parseInt(prev.birthMonth);
        if (year && month) {
          const daysInMonth = new Date(year, month, 0).getDate();
          if (parseInt(prev.birthDay) > daysInMonth) {
            newData.birthDay = '';
          }
        }
      }
      
      return newData;
    });
  };

  const handleIconUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ファイルバリデーション
    const validation = validateImageFile(file, 5 * 1024 * 1024); // 5MB制限
    if (!validation.valid) {
      setError(validation.error);
      e.target.value = ''; // ファイル選択をクリア
      return;
    }

    setIconFile(file);
    setUseGoogleIcon(false);
    
    // プレビュー表示
    const reader = new FileReader();
    reader.onload = (e) => {
      setIconPreview(e.target.result);
    };
    reader.readAsDataURL(file);
    setError('');
  };

  const handleUseGoogleIcon = () => {
    if (currentUser?.photoURL) {
      setIconPreview(currentUser.photoURL);
      setUseGoogleIcon(true);
      setIconFile(null);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // バリデーション
      if (!formData.nickname.trim()) {
        setError('ニックネームを入力してください');
        setLoading(false);
        return;
      }

      if (!formData.gender) {
        setError('性別を選択してください');
        setLoading(false);
        return;
      }

      if (!formData.birthYear || !formData.birthMonth || !formData.birthDay) {
        setError('生年月日を入力してください');
        setLoading(false);
        return;
      }

      // 生年月日を結合してISO形式に変換
      const birthdate = `${formData.birthYear}-${formData.birthMonth.padStart(2, '0')}-${formData.birthDay.padStart(2, '0')}`;

      let iconURL;
      // カスタムアイコンがアップロードされている場合
      if (iconFile && !useGoogleIcon) {
        try {
          const iconResult = await uploadUserIcon(currentUser.uid, iconFile);
          iconURL = iconResult.url;
          // アイコンアップロード成功
        } catch (uploadError) {
          console.error('アイコンアップロードエラー:', uploadError);
          const errorMessage = getUploadErrorMessage(uploadError);
          setError(`アイコンのアップロードに失敗しました: ${errorMessage}`);
          setLoading(false);
          return;
        }
      } else {
        // 変更しない場合は既存アイコンを優先
        iconURL = userProfile?.iconURL || currentUser?.photoURL || '/img/icon.png';
      }

      // Firestoreのユーザープロフィールを更新
      const userInfo = {
        nickname: formData.nickname.trim(),
        gender: formData.gender,
        birthdate: birthdate,
        iconURL: iconURL,
        profileCompleted: true,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'users', currentUser.uid), userInfo);
      // プロフィール設定完了

      // Firestoreから最新ユーザーデータを取得しContextも更新
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          // ProfileSetup: updateUserProfile called
          updateUserProfile(currentUser.uid, userSnap.data());
        } else {
          console.warn('[ProfileSetup] userSnap does not exist for:', currentUser.uid);
        }
      } catch (fetchError) {
        console.error('ユーザープロフィール再取得エラー:', fetchError);
      }

      // Top画面に遷移
      navigate('/top');
    } catch (error) {
      console.error('プロフィール設定エラー:', error);
      setError('プロフィール設定に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Googleログイン直後の遷移以外では初期化しない
    if (location.state?.fromGoogleLogin) {
      // 最小限の情報でプロフィール作成
      const basicInfo = {
        nickname: currentUser?.displayName || 'ユーザー',
        iconURL: currentUser?.photoURL || '/img/icon.png',
        profileCompleted: false,
        updatedAt: new Date().toISOString()
      };
      updateDoc(doc(db, 'users', currentUser.uid), basicInfo)
        .then(() => {
          // ProfileSetup: handleSkip updateUserProfile called
          updateUserProfile(currentUser.uid, basicInfo);
          navigate('/top');
        })
        .catch((error) => {
          console.error('基本プロフィール作成エラー:', error);
          navigate('/top'); // エラーでも遷移
        });
    } else {
      // 何もせず遷移のみ
      navigate('/top');
    }
  };

  if (!currentUser) {
    return <div>ログインが必要です</div>;
  }

  return (
    <div className="profile-container">
      <div className="profile-setup-container">
        <img src="/img/tiny-identity.png" alt="Tiny identity ロゴ" className="profile-setup-logo" />
        <h1 className="profile-setup-title">Tiny identity</h1>
    
    {error && (
        <div className="profile-setup-error">
        {error}
        </div>
    )}
    
    <form className="profile-setup-form" onSubmit={handleSubmit}>
        {/* アイコンとニックネーム（横並び） */}
        <div className="form-row nickname-row">
          <div className="icon-upload">
            <label htmlFor="icon-upload-input">
              <img src={iconPreview} alt="プロフィール画像" className="icon-preview" />
            </label>
            <input
              type="file"
              id="icon-upload-input"
              accept="image/jpeg,image/png,image/gif"
              onChange={handleIconUpload}
              disabled={loading}
            />
          </div>
          <div className="nickname-input">
            <label htmlFor="nickname">ニックネーム</label>
            <input
              type="text"
              id="nickname"
              name="nickname"
              value={formData.nickname}
              onChange={handleInputChange}
              placeholder="表示名を入力してください"
              maxLength="20"
              disabled={loading}
              required
            />
          </div>
        </div>

        {/* 性別 */}
        <div className="profile-setup-field">
        <label>性別</label>
        <div className="profile-setup-radio-group">
            <label className="profile-setup-radio">
            <input
                type="radio"
                name="gender"
                value="male"
                checked={formData.gender === 'male'}
                onChange={handleInputChange}
            />
            <span>男性</span>
            </label>
            <label className="profile-setup-radio">
            <input
                type="radio"
                name="gender"
                value="female"
                checked={formData.gender === 'female'}
                onChange={handleInputChange}
            />
            <span>女性</span>
            </label>
            <label className="profile-setup-radio">
            <input
                type="radio"
                name="gender"
                value="other"
                checked={formData.gender === 'other'}
                onChange={handleInputChange}
            />
            <span>その他</span>
            </label>
        </div>
        </div>

        {/* 生年月日 */}
        <div className="profile-setup-field">
        <label>生年月日</label>
        <div className="birth-date-container">
          <select 
            name="birthYear" 
            value={formData.birthYear}
            onChange={handleInputChange}
            className="birth-select"
            required
          >
            <option value="">年</option>
            {generateYears().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <span className="birth-separator">年</span>
          
          <select 
            name="birthMonth" 
            value={formData.birthMonth}
            onChange={handleInputChange}
            className="birth-select"
            required
          >
            <option value="">月</option>
            {generateMonths().map(month => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>
          <span className="birth-separator">月</span>
          
          <select 
            name="birthDay" 
            value={formData.birthDay}
            onChange={handleInputChange}
            className="birth-select"
            disabled={!formData.birthYear || !formData.birthMonth}
            required
          >
            <option value="">日</option>
            {generateDays().map(day => (
              <option key={day} value={day}>{day}</option>
            ))}
          </select>
          <span className="birth-separator">日</span>
        </div>
        </div>

        {/* ボタン */}
        <button
            type="submit"
            className="btn-primary"
            disabled={loading}
        >
            {loading ? '設定中...' : 'プロフィールを完成させる'}
        </button>
        
        {/* Googleログイン直後の遷移時のみ「後で設定する」ボタンを表示 */}
        {location.state?.fromGoogleLogin && (
          <button
              type="button"
              className="btn-outline"
              onClick={handleSkip}
              disabled={loading}
          >
              後で設定する
          </button>
        )}
    </form>
    </div>
    </div>
  );
};

export default ProfileSetup;
